import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

/**
 * Ensures event page content stays fresh during long-running events.
 *
 * During a tasting event, participants may put their phone down for 10-20 minutes
 * between interactions. When they pick it up again, the WebSocket connection may
 * have dropped and the page could be stale.
 *
 * This controller uses the Page Visibility API to detect when the user returns
 * to the page after being away. If they've been gone longer than the configured
 * threshold, it automatically refreshes the page via Turbo to ensure fresh content.
 *
 * Usage:
 *   <div data-controller="event-freshness"
 *        data-event-freshness-stale-after-minutes-value="5">
 *     <!-- event content -->
 *   </div>
 *
 * @example With custom threshold
 *   <div data-controller="event-freshness"
 *        data-event-freshness-stale-after-minutes-value="10">
 */
export default class extends Controller {
  static values = {
    staleAfterMinutes: { type: Number, default: 5 }
  }

  connect() {
    this.hiddenAt = null
    this.boundHandler = this.handleVisibilityChange.bind(this)
    document.addEventListener("visibilitychange", this.boundHandler)
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this.boundHandler)
  }

  /**
   * Handles page visibility changes.
   *
   * When page becomes hidden (phone locked, tab switched), records the timestamp.
   * When page becomes visible again, checks if it's been longer than the threshold.
   * If so, triggers a Turbo visit to refresh the page content.
   */
  handleVisibilityChange() {
    if (document.hidden) {
      // Page is now hidden - record when
      this.hiddenAt = Date.now()
    } else if (this.hiddenAt) {
      // Page is now visible - check if we should refresh
      const minutesHidden = (Date.now() - this.hiddenAt) / 60000
      
      if (minutesHidden >= this.staleAfterMinutesValue) {
        // Been hidden too long - refresh to get fresh content
        // Using replace action so we don't pollute browser history
        Turbo.visit(window.location.href, { action: "replace" })
      }
      
      this.hiddenAt = null
    }
  }
}
